/**
 * Wizard Types and Constants
 *
 * Shared types for the assessment creation wizard.
 */

export type SourceType = 'document' | 'guided' | 'freetext';

export interface GuidedAnswers {
  coreSkill?: string;
  learnerContext?: string;
  businessOutcome?: string;
  situations?: string;
  constraints?: string;
  successCriteria?: string;
}

export interface WizardState {
  // Step 1: Basic Details
  name: string;
  objective: string;
  assessmentTiming: 'pre' | 'post';
  assessmentMode: 'voice' | 'quiz';
  agentPrompt: string;

  // Step 2: Source Selection
  sourceType: SourceType;

  // Step 3A: Document Upload
  documentText: string;
  documentFileName: string;

  // Step 3B: Guided Questions
  guidedAnswers: GuidedAnswers;

  // Step 3C: Free Text (legacy)
  additionalContext: string;

  // Step 4: Advanced Options
  selectedRubricId: string;
  showScoreToLearner: boolean;
  externalReferenceId: string;
  sourceSystem: string;
}

export const WIZARD_LIMITS = {
  NAME_MAX: 200,
  OBJECTIVE_MAX: 2000,
  DOCUMENT_TEXT_MAX: 8000,
  GUIDED_ANSWER_MAX: 500,
  ADDITIONAL_CONTEXT_MAX: 2000,
  FILE_SIZE_MAX: 10 * 1024 * 1024, // 10MB
  PREVIEW_LENGTH: 500,
} as const;

export const INITIAL_WIZARD_STATE: WizardState = {
  name: '',
  objective: '',
  assessmentTiming: 'pre',
  assessmentMode: 'voice',
  agentPrompt: '',
  sourceType: 'guided',
  documentText: '',
  documentFileName: '',
  guidedAnswers: {},
  additionalContext: '',
  selectedRubricId: '',
  showScoreToLearner: false,
  externalReferenceId: '',
  sourceSystem: '',
};

export const GUIDED_QUESTIONS = [
  {
    key: 'coreSkill' as const,
    label: 'Core Skill',
    question: 'What is the primary skill or competency being assessed?',
    placeholder: 'e.g., Active listening, Objection handling, Problem diagnosis',
  },
  {
    key: 'learnerContext' as const,
    label: 'Learner Context',
    question: 'Who are the learners and what is their role?',
    placeholder: 'e.g., New customer service representatives in their first 90 days',
  },
  {
    key: 'businessOutcome' as const,
    label: 'Business Outcome',
    question: 'What business outcome should successful completion achieve?',
    placeholder: 'e.g., Improved first-call resolution rate, reduced escalations',
  },
  {
    key: 'situations' as const,
    label: 'Situations',
    question: 'Describe typical situations where this skill is applied',
    placeholder: 'e.g., Customer calls about billing error, product not working as expected',
  },
  {
    key: 'constraints' as const,
    label: 'Constraints',
    question: 'What constraints or boundaries should the assessment respect?',
    placeholder: 'e.g., Cannot offer refunds over $50, must follow script for compliance',
  },
  {
    key: 'successCriteria' as const,
    label: 'Success Criteria',
    question: 'How would you recognize excellent performance?',
    placeholder: 'e.g., Empathetic tone, identifies root cause, offers solution within policy',
  },
] as const;
